// src/pages/Vacancies.jsx
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

const float = keyframes`
  0% { transform: translateY(0) }
  50% { transform: translateY(-4px) }
  100% { transform: translateY(0) }
`;

const PageWrap = styled.div`
  --gap: 16px;
`;

const Hero = styled.section`
  position: relative; overflow: hidden;
  padding: clamp(48px, 8vw, 96px) 0;
  .bg { position:absolute; inset:-20%;
    background:
      radial-gradient(40% 40% at 20% 0%, #ff660022, transparent 60%),
      radial-gradient(35% 35% at 80% 10%, #00c2ff22, transparent 60%),
      radial-gradient(50% 50% at 50% 120%, #9bff0024, transparent 60%);
    filter: blur(40px); transform: translateZ(0);
  }
  .container { width:${({theme})=>theme.container}; margin:0 auto; display:grid; grid-template-columns: 1.1fr .9fr; gap: 24px; }
  @media (max-width: 980px){ .container{ grid-template-columns: 1fr; } }

  h1 { font-size:${({theme})=>theme.font.sizes.h1}; margin:0 0 10px; }
  p.lead { color:${({theme})=>theme.textMuted}; margin:0 0 18px; font-size: clamp(16px, 2.2vw, 20px); }
  .cta { display:flex; gap: 10px; flex-wrap:wrap; }
`;

const Card = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
`;

const Section = styled.section`
  padding: 56px 0;
  &.muted { background:${({theme})=>theme.body}; }
  .container { width:${({theme})=>theme.container}; margin:0 auto; }
  .section-head { display:flex; align-items:end; justify-content:space-between; gap:16px; margin-bottom:16px; }
  .kicker { font-size:13px; letter-spacing:.12em; text-transform:uppercase; opacity:.7; }
`;

const Filters = styled(Card)`
  padding: 14px;
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: var(--gap);
  @media (max-width:980px){ grid-template-columns: 1fr 1fr; }
  select, input{
    width:100%; padding:10px 12px; border-radius:${({theme})=>theme.radius.md}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface}; color:inherit;
  }
  .right { display:flex; gap:8px; align-items:center; justify-content:flex-end; grid-column: -2 / -1; }
  @media (max-width:980px){ .right{ grid-column: 1 / -1; justify-content:flex-start; } }
  .btn { padding:10px 12px; border-radius:${({theme})=>theme.radius.pill}px; border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface}; cursor:pointer; }
`;

const JobsGrid = styled.div`
  display:grid; grid-template-columns: 1fr; gap: var(--gap);
`;

const JobCard = styled(Card)`
  padding: 16px; display:grid; grid-template-columns: 1fr auto; gap: 12px; align-items:start;
  border-left:4px solid ${({theme})=>theme.accent}; overflow:hidden; position:relative;
  .meta { display:flex; flex-wrap:wrap; gap:8px; color:${({theme})=>theme.textMuted}; font-size:14px; }
  .badge { padding:4px 8px; border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.pill}px; }
  .tags { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
  .tag { font-size:12px; padding:4px 8px; border-radius:${({theme})=>theme.radius.pill}px; background:${({theme})=>theme.body}; border:1px dashed ${({theme})=>theme.border}; }
  .actions { display:flex; gap:8px; flex-wrap:wrap; }
  .btn { padding:8px 12px; border-radius:${({theme})=>theme.radius.pill}px; border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface}; cursor:pointer; }
  .btn--primary { background: var(--accent, ${({theme})=>theme.accent}); color: #fff; border-color: transparent; }
  .spark { position:absolute; right:-60px; top:-60px; width:160px; height:160px; border-radius:50%;
    background: radial-gradient(50% 50% at 50% 50%, rgba(255,102,0,.25), transparent 70%);
    filter: blur(16px); animation:${float} 6s ease-in-out infinite; pointer-events:none;
  }
`;

const Perks = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: var(--gap);
  @media (max-width:980px){ grid-template-columns: 1fr; }
  .perk { ${Card} ; padding:14px; }
  .perk .icon{ width:40px; height:40px; border-radius:12px; border:1px solid ${({theme})=>theme.border}; display:grid; place-items:center; margin-bottom:8px; background:${({theme})=>theme.body}; }
  .perk p{ color:${({theme})=>theme.textMuted}; margin:6px 0 0; }
`;

const Steps = styled.ol`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: var(--gap);
  counter-reset: s; list-style:none; margin:0; padding:0;
  @media (max-width:980px){ grid-template-columns: 1fr; }
  li { ${Card}; padding:16px; counter-increment:s; }
  li b::before{ content: counter(s) '. '; color:${({theme})=>theme.accent}; font-weight:800; }
  li p{ color:${({theme})=>theme.textMuted}; margin:6px 0 0; }
`;

const Faq = styled.div`
  display:grid; gap: var(--gap);
  .q { ${Card}; overflow:hidden; }
  .q button { width:100%; text-align:left; padding:14px; background:none; border:none; display:flex; align-items:center; justify-content:space-between; cursor:pointer; font-weight:700; }
  .q .a { padding: 0 14px 14px; color:${({theme})=>theme.textMuted}; }
`;

const ApplyForm = styled(Card)`
  padding:16px; display:grid; grid-template-columns: 1fr 1fr; gap: var(--gap);
  @media (max-width:980px){ grid-template-columns: 1fr; }
  input, select, textarea{
    width:100%; padding:10px 12px; border-radius:${({theme})=>theme.radius.md}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface}; color:inherit;
  }
  textarea{ grid-column: 1 / -1; min-height: 96px; }
  .row { grid-column: 1 / -1; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
  .btn { padding:10px 14px; border-radius:${({theme})=>theme.radius.pill}px; border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface}; cursor:pointer; }
  .btn--primary{ background: var(--accent, ${({theme})=>theme.accent}); color:#fff; border-color:transparent; }
`;

function Icon({name='bolt'}) {
  const icons = {
    money:  <path d="M4 7h16v10H4V7zm2 2v6h12V9H6zm8-5h2v3h-2V4zM8 4h2v3H8V4z"/>,
    clock:  <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5h-2v6l5 3 1-1.73-4-2.27V7z"/>,
    remote: <path d="M7 3h10v18H7V3zm2 3h6v2H9V6zm0 4h6v2H9v-2zm0 4h6v2H9v-2z"/>,
    learn:  <path d="M4 6l8-3 8 3v6c0 5-8 7-8 7S4 17 4 12V6zm8-1l6 2.2V12c0 3.5-6 5-6 5s-6-1.5-6-5V7.2L12 5z"/>,
    kit:    <path d="M4 8h16v10H4V8zm3-4h10l2 2H5l2-2z"/>,
    bolt:   <path d="M13 2L4 14h6v8l9-12h-6V2z"/>,
  };
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">{icons[name]||icons.bolt}</svg>;
}

const JOBS = [
  {
    id:'cctv-eng',
    title:'Інженер з відеоспостереження',
    dept:'Системи безпеки',
    location:'Київ',
    type:'Повна зайнятість',
    salary:'35 000–55 000 ₴',
    tags:['CCTV','IP-камери','NVR','PoE','MikroTik'],
    summary:'Проєктування та монтаж систем відеоспостереження на об’єктах промисловості та логістики.',
    responsibilities:[
      'Підбір обладнання, складання схем живлення/мережі',
      'Монтаж, налаштування, інтеграція з ACS',
      'Техпідтримка, плановий сервіс'
    ],
    requirements:[
      'Досвід з IP-камерами/регистраторами 2+ роки',
      'Базові мережеві знання (L2/L3, VLAN)',
      'Водійське посвідчення кат. B — буде плюсом'
    ],
  },
  {
    id:'acs-tech',
    title:'Монтажник систем контролю доступу',
    dept:'Системи безпеки',
    location:'Київ/область',
    type:'Повна зайнятість',
    salary:'30 000–45 000 ₴',
    tags:['СКД','турнікети','шлагбауми','ZKTeco','Hikvision'],
    summary:'Монтаж і сервіс СКД: турнікети, зчитувачі, замки, інтеграції з відео.',
    responsibilities:[
      'Кабельні траси, встановлення обладнання',
      'Пусконалагодження та база користувачів',
      'Навчання клієнта'
    ],
    requirements:[
      'Впевнені навички інсталяції слаботочки',
      'Акуратність, відповідальність, ТБ'
    ],
  },
  {
    id:'fabrication',
    title:'Слюсар-збирач (металообробка)',
    dept:'Виробництво',
    location:'Черкаси',
    type:'Зміни/повна',
    salary:'35 000–60 000 ₴',
    tags:['MIG/MAG','TIG','збірка','оснастка'],
    summary:'Збірка металоконструкцій за кресленнями, підготовка під зварювання/фарбування.',
    responsibilities:[
      'Розмітка, різання, свердління, підгонка',
      'Збірка по КД, контроль якості'
    ],
    requirements:[
      'Читання креслень, допуски',
      'Досвід 2+ роки'
    ],
  },
  {
    id:'furniture',
    title:'Майстер меблевого виробництва',
    dept:'Меблі/HPL',
    location:'Бровари',
    type:'Повна зайнятість',
    salary:'30 000–50 000 ₴',
    tags:['МДФ','HPL','фурнітура','CAD'],
    summary:'Виготовлення та монтаж корпусних меблів, HPL-облицювання.',
    responsibilities:[
      'Порізка, кромкування, збирання',
      'Монтаж на об’єктах'
    ],
    requirements:[
      'Досвід з плитними матеріалами',
      'Охайність, клієнтоорієнтованість'
    ],
  },
];

const DEPTS = ['Усі відділи','Системи безпеки','Виробництво','Меблі/HPL'];
const LOCS = ['Будь-яка','Київ','Київ/область','Бровари','Черкаси'];
const TYPES = ['Будь-який','Повна зайнятість','Зміни/повна'];

export default function Vacancies() {
  const meta = usePageMeta('vacancies');
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [dept, setDept] = useState(DEPTS[0]);
  const [loc, setLoc] = useState(LOCS[0]);
  const [type, setType] = useState(TYPES[0]);
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(()=>{ AOS.init({ once:true, duration:700, offset:80, easing:'ease-out-cubic' }); },[]);

  const filtered = useMemo(()=>{
    return JOBS.filter(j=>{
      const text = (j.title+' '+j.summary+' '+j.tags.join(' ')).toLowerCase();
      const byQ = !q || text.includes(q.toLowerCase());
      const byDept = dept===DEPTS[0] || j.dept===dept;
      const byLoc = loc===LOCS[0] || j.location===loc;
      const byType = type===TYPES[0] || j.type===type;
      return byQ && byDept && byLoc && byType;
    });
  }, [q, dept, loc, type]);

  const onApplyQuick = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = fd.get('name')||'';
    const email = fd.get('email')||'';
    const role = fd.get('role')||'';
    const msg = fd.get('msg')||'';
    const subject = encodeURIComponent(`Відгук на вакансію: ${role} — ${name}`);
    const body = encodeURIComponent(`${msg}\n\nПІБ: ${name}\nEmail: ${email}\nПосада: ${role}`);
    window.location.href = `mailto:hr@momv.com.ua?subject=${subject}&body=${body}`;
  };

  return (
    <PageWrap>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/info/vacancies'}
      />
      <Hero>
        <div className="bg" aria-hidden />
        <div className="container">
          <div data-aos="fade-up">
            <h1>Вакансії MOMV</h1>
            <p className="lead">Приєднуйтесь до команди інженерів, монтажників та виробничників, які збирають системи «під ключ» для промисловості, логістики та девелопменту.</p>
            <div className="cta" style={{display:'flex', gap:10, flexWrap:'wrap'}}>
              <a className="btn btn--primary" href="#open-roles">Відкриті позиції</a>
              <a className="btn btn--ghost" href="#how-we-hire">Як ми наймаємо</a>
            </div>
          </div>
          <div data-aos="fade-left" style={{display:'grid', placeItems:'center'}}>
            <Card style={{width:'min(520px,90%)', height:'220px', display:'grid', placeItems:'center', borderStyle:'dashed'}}>
              <div style={{textAlign:'center', color:'var(--textMuted)'}}>
                <div style={{fontWeight:800, fontSize:18}}>Будуймо інженерію разом</div>
                <div style={{fontSize:14, marginTop:6}}>CCTV • СКД • Ворота • Електрика • Вентиляція • Меблі/HPL</div>
              </div>
            </Card>
          </div>
        </div>
      </Hero>

      {/* Filters + list */}
      <Section id="open-roles" className="muted">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Відкриті позиції</div>
              <h2>Знайдіть роль для себе</h2>
            </div>
          </div>

          <Filters data-aos="fade-up">
            <input placeholder="Пошук за назвою / навичками" value={q} onChange={e=>setQ(e.target.value)} />
            <select value={dept} onChange={e=>setDept(e.target.value)}>
              {DEPTS.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            <select value={loc} onChange={e=>setLoc(e.target.value)}>
              {LOCS.map(l=><option key={l} value={l}>{l}</option>)}
            </select>
            <select value={type} onChange={e=>setType(e.target.value)}>
              {TYPES.map(tp=><option key={tp} value={tp}>{tp}</option>)}
            </select>
          </Filters>

          <div style={{height:10}} />

          <JobsGrid>
            {filtered.map((j,i)=>(
              <JobCard key={j.id} data-aos="fade-up" data-aos-delay={i%4*80} itemScope itemType="https://schema.org/JobPosting">
                <meta itemProp="identifier" content={j.id}/>
                <div className="spark" aria-hidden />
                <div>
                  <h3 itemProp="title" style={{margin:'0 0 6px'}}>{j.title}</h3>
                  <div className="meta">
                    <span className="badge" itemProp="employmentType">{j.type}</span>
                    <span className="badge" itemProp="jobLocation">{j.location}</span>
                    <span className="badge" itemProp="department">{j.dept}</span>
                    {j.salary && <span className="badge" itemProp="baseSalary">{j.salary}</span>}
                  </div>
                  <p style={{margin:'10px 0 0'}} itemProp="description">{j.summary}</p>
                  <div className="tags">
                    {j.tags.map(tg=><span key={tg} className="tag">{tg}</span>)}
                  </div>
                </div>
                <div className="actions">
                  <a className="btn btn--primary" href={`mailto:hr@momv.com.ua?subject=${encodeURIComponent('Резюме: '+j.title)}&body=${encodeURIComponent('Вітаю! Надсилаю резюме на позицію "'+j.title+'".\n\nПосилання на резюме/CV:\nПортфоліо:\nКонтакти:')}`}>Відгукнутись</a>
                  <a className="btn" href={`#${j.id}-details`}>Деталі</a>
                </div>
              </JobCard>
            ))}
            {filtered.length===0 && (
              <Card style={{padding:16}} data-aos="fade-up">
                Наразі під ваші фільтри немає позицій. Скиньте резюме на <a href="mailto:hr@momv.com.ua">hr@momv.com.ua</a> — ми збережемо в резерв.
              </Card>
            )}
          </JobsGrid>

          {/* Details blocks */}
          <div style={{height:24}} />
          {JOBS.map((j,i)=>(
            <Card id={`${j.id}-details`} key={j.id} style={{padding:16, marginBottom:16}} data-aos="fade-up" data-aos-delay={(i%4)*60}>
              <h3 style={{margin:'0 0 8px'}}>{j.title}: обовʼязки та вимоги</h3>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
                <div>
                  <b>Обовʼязки</b>
                  <ul style={{margin:'8px 0 0 18px'}}>
                    {j.responsibilities.map((r,idx)=><li key={idx}>{r}</li>)}
                  </ul>
                </div>
                <div>
                  <b>Вимоги</b>
                  <ul style={{margin:'8px 0 0 18px'}}>
                    {j.requirements.map((r,idx)=><li key={idx}>{r}</li>)}
                  </ul>
                </div>
              </div>
            </Card>
          ))}

        </div>
      </Section>

      {/* Perks / benefits */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Переваги</div><h2>Чому MOMV</h2></div>
          </div>
          <Perks>
            {[
              {icon:'money', h:'Стабільна оплата', p:'Офіційне оформлення, прозорі грейди, своєчасні виплати.'},
              {icon:'learn', h:'Ріст та навчання', p:'Навчання на реальних об’єктах, наставництво, курси/сертифікації.'},
              {icon:'remote', h:'Гнучкість', p:'Об’єкти по Києву/області і відрядження — обговорюємо індивідуально.'},
              {icon:'kit', h:'Інструмент', p:'Забезпечимо інструментом та спецодягом. Є службове авто для виїздів.'},
              {icon:'clock', h:'Баланс', p:'Планові графіки, 24/7 підтримка змін, безпека понад усе.'},
              {icon:'bolt', h:'Проекти-флагмани', p:'Складні інтеграції, цікаві задачі, портфоліо гучних брендів.'},
            ].map((pk,i)=>(
              <div className="perk" key={i} data-aos="fade-up" data-aos-delay={i%3*60}>
                <div className="icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><Icon name={pk.icon}/></svg></div>
                <b>{pk.h}</b>
                <p>{pk.p}</p>
              </div>
            ))}
          </Perks>
        </div>
      </Section>

      {/* Hiring process */}
      <Section id="how-we-hire" className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Процес</div><h2>Як ми наймаємо</h2></div>
          </div>
          <Steps>
            <li data-aos="fade-up"><b>Заявка/резюме</b><p>Надішліть коротко про досвід і контакти. Портфоліо вітається.</p></li>
            <li data-aos="fade-up" data-aos-delay="80"><b>Онлайн-розмова</b><p>15–25 хв з HR/керівником, швидке взаємне знайомство.</p></li>
            <li data-aos="fade-up" data-aos-delay="160"><b>Технічна</b><p>Перевірка навичок і міні-завдання (за потреби) або виїзд на обʼєкт.</p></li>
            <li data-aos="fade-up" data-aos-delay="240"><b>Офер</b><p>Узгоджуємо умови, графік, старт, оформлення.</p></li>
          </Steps>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">FAQ</div><h2>Питання кандидатів</h2></div>
          </div>
          <Faq>
            {[
              {q:'Чи потрібні відрядження?', a:'Час від часу — так, заздалегідь узгоджуємо. Доступний компенсований транспорт/проживання.'},
              {q:'Який графік?', a:'Стандарт — пн-пт, 9:00–18:00. Монтажні зміни можливі під обʼєкт — з доплатами.'},
              {q:'Оплата інструменту/ПММ?', a:'Інструмент надаємо. ПММ/амортизація компенсуються за політикою компанії.'},
              {q:'Стажування?', a:'Так. Для джуніорів передбачено наставництво та оплачуваний випробувальний термін.'},
            ].map((it,idx)=>(
              <div className="q" key={idx} data-aos="fade-up" data-aos-delay={idx*70}>
                <button aria-expanded={openFaq===idx} onClick={()=>setOpenFaq(openFaq===idx?-1:idx)}>
                  <span>{it.q}</span><span>{openFaq===idx?'−':'+'}</span>
                </button>
                {openFaq===idx && <div className="a">{it.a}</div>}
              </div>
            ))}
          </Faq>
        </div>
      </Section>

      {/* Quick apply */}
      <Section className="muted" id="apply">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Заявка</div><h2>Відгукнутися швидко</h2></div>
          </div>
          <ApplyForm as="form" onSubmit={onApplyQuick}>
            <input name="name" type="text" placeholder="Ваше ім’я *" required />
            <input name="email" type="email" placeholder="Email *" required />
            <select name="role" defaultValue="">
              <option value="" disabled>Обрати позицію</option>
              {JOBS.map(j=><option key={j.id} value={j.title}>{j.title}</option>)}
              <option value="Резерв/Інше">Резерв/Інше</option>
            </select>
            <input name="phone" type="tel" placeholder="Телефон" />
            <textarea name="msg" placeholder="Коротко опишіть досвід, посилання на резюме/портфоліо"></textarea>
            <div className="row">
              <button className="btn btn--primary" type="submit">Надіслати</button>
              <a className="btn" href="mailto:hr@momv.com.ua">hr@momv.com.ua</a>
            </div>
          </ApplyForm>
        </div>
      </Section>
    </PageWrap>
  );
}
